<template>
    <q-page :padding="!user.id && $q.screen.gt.xs" class="docs-table" :style-fn="myTweak">
        <q-table
            :title="user.id ? 'Orders of ' + this.full_name : $t('transfers.menu.index')"
            ref="table"
            :data="serverData"
            :columns="columns"
            :filter="filter"
            row-key="reference"
            :pagination.sync="serverPagination"
            :visible-columns="visibleColumns"
            :loading="loading"
            :selection="selection"
            :selected.sync="selectedRows"
            class="card-shadow"
            :rows-per-page-options="[10, 15, 30, 50, 100]"
            @request="all"
        >
            <template v-slot:top-right="props" class="row q-col-gutter-lg">
                <q-btn-dropdown
                    outline
                    color="primary"
                    label="CM"
                    class="q-mr-sm"
                >
                    <q-list>
                        <q-item
                            clickable
                            v-close-popup
                            @click="displayCmWithdrawalDialog = true"
                            v-if="$auth.can('transfers.crown_money_withdrawal')"
                        >
                            <q-item-section>
                                <q-item-label>{{$t('transfers.cm_withdrawals')}}</q-item-label>
                            </q-item-section>
                        </q-item>

                        <q-item
                            clickable
                            v-close-popup
                            @click="cmIncomingPayments"
                        >
                            <q-item-section>
                                <q-item-label>{{$t('transfers.cm_transactions')}}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-list>
                </q-btn-dropdown>

                <q-btn
                    outline
                    :label="$t('new')"
                    icon="fa fa-plus"
                    color="positive"
                    class="q-mr-sm"
                    v-if="$auth.can('transfers.create')"
                    @click="$router.push({name: 'transfers.create.advanced'})"
                />
                <q-btn outline :label="$t('refresh')" icon="fas fa-sync" color="primary" class="q-mr-sm" @click="all({pagination: serverPagination, filter})" :loading="loading">
                    <template v-slot:loading>
                        <q-spinner-dots />
                    </template>
                </q-btn>
                <q-btn
                    :outline="!is_filtered"
                    color="black"
                    :label="is_filtered ? 'Filtered' : 'Filter'"
                    class="q-mr-sm"
                    icon="fa fa-filter"
                >
                    <q-menu>
                        <div class="row q-pa-md q-col-gutter-md" style="width: 300px">
                            <div class="col-12">
                                <q-input
                                    :value="date_range.from ? date_range.from + ' | ' + date_range.to : ''"
                                    dense
                                    class="q-mr-sm"
                                    color="primary"
                                    label="Date range"
                                    readonly
                                >
                                    <template v-slot:append v-if="date_range.from" >
                                        <q-icon
                                            name="cancel"
                                            @click.stop="removeDateRangeFilter"
                                            class="cursor-pointer"
                                        />
                                    </template>
                                    <template v-slot:prepend>
                                        <q-icon name="event" class="cursor-pointer">
                                            <q-popup-proxy
                                                ref="qDateProxy"
                                                transition-show="scale"
                                                transition-hide="scale"
                                                @hide="all({ pagination: serverPagination, filter})"
                                            >
                                                <q-date
                                                    v-model="selecting_range"
                                                    range
                                                    mask="YYYY-MM-DD"
                                                >
                                                    <div class="row items-center justify-end">
                                                        <q-btn v-close-popup label="Set" color="primary" flat />
                                                    </div>
                                                </q-date>
                                            </q-popup-proxy>
                                        </q-icon>
                                    </template>
                                </q-input>
                            </div>
                            <div class="col-12">
                                <q-select
                                    v-model="filtered_statuses"
                                    multiple
                                    dense
                                    options-dense
                                    :display-value="filtered_statuses.length === 6 ? 'Statuses' : 'Statuses (filtered)'"
                                    emit-value
                                    map-options
                                    :options="statuses"
                                    option-value="name"
                                    style="min-width: 150px;margin-right:10px"
                                    @blur="all({ pagination: serverPagination, filter})"
                                    color="primary"
                                >
                                    <template v-slot:option="{ itemProps, itemEvents, opt, selected, toggleOption }">
                                        <q-item
                                            v-bind="itemProps"
                                            v-on="itemEvents"
                                        >
                                            <q-item-section>
                                                <q-item-label v-html="opt.label" ></q-item-label>
                                            </q-item-section>
                                            <q-item-section side>
                                                <q-toggle :value="selected" @input="toggleOption(opt)" color="green"/>
                                            </q-item-section>
                                        </q-item>
                                    </template>
                                </q-select>
                            </div>
                            <div class="col-12">
                                <q-select
                                    v-model="origin"
                                    label="From"
                                    clearable
                                    dense
                                    options-dense
                                    :options="origins"
                                    style="min-width: 150px;margin-right:10px"
                                    color="primary"
                                    @input="all({ pagination: serverPagination, filter})"
                                />
                            </div>
                            <div class="col-12">
                                <q-select
                                    v-model="dest"
                                    label="To"
                                    clearable
                                    dense
                                    options-dense
                                    :options="destinations"
                                    style="min-width: 150px;margin-right:10px"
                                    color="primary"
                                    @input="all({ pagination: serverPagination, filter})"
                                />
                            </div>
                        </div>
                    </q-menu>
                </q-btn>
                <q-input dense debounce="1250" v-model="filter" :placeholder="$t('search')" class="q-mr-sm" color="primary">
                    <template v-slot:prepend>
                        <q-icon name="search" />
                    </template>
                </q-input>
                <q-select
                    v-model="visibleColumns"
                    multiple
                    dense
                    options-dense
                    :display-value="$q.lang.table.columns"
                    emit-value
                    map-options
                    :options="columns"
                    option-value="name"
                    style="min-width: 150px;margin-right:10px"
                    color="primary"
                />
            </template>

            <!-- START CUSTOM COLUMNS -->
            <template v-slot:header="props">
                <q-tr :props="props">
                    <q-th auto-width />
                    <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                    >
                        <span>{{ col.label }}</span>
                    </q-th>
                </q-tr>
            </template>

            <template v-slot:body="props">
                <q-tr :props="props" :statement="props.row.currentStatus = statuses.find(item => item.name === props.row.status)">
                    <q-td auto-width>
                        <q-btn size="sm" color="primary" round dense @click="details(props)" :icon="props.expand ? 'fa fa-angle-up' : 'fa fa-angle-down'" />
                    </q-td>
                    <q-td key="reference" :props="props">
                        <div class="text-bold" >{{props.row.reference}}
                            <q-btn icon="far fa-copy" size="xs" color="primary"v-clipboard="props.row.reference" @click="$copied('Order tracking code copied!')" round flat />
                        </div>
                        <small>
                            {{props.row.registered_at}}
                            <q-tooltip
                                anchor="top middle"
                                self="center middle"
                                content-class="bg-amber-2 text-black"
                                content-style="font-size: 13px"
                            >{{props.row.registered_at_utc}} (UTC)</q-tooltip>
                        </small>
                    </q-td>
                    <q-td key="incoming_details" :props="props" class="no-padding text-center">
                        <q-item>
                            <q-item-section avatar>
                                <q-btn dense round flat size="md" color="primary" icon="far fa-file-lines" @click="openReceipts(props.row)" v-if="props.row.has_receipt"/>
                            </q-item-section>
                            <q-item-section>
                                <q-item-label lines="1" v-if="$auth.can('users.send_email_when_transfer_status_changed')">
                                    <span v-clipboard="props.row.user.name" @click="$copied('Sender\'s name copied!')">{{ props.row.user.name }}</span>
                                    <q-btn icon="far fa-envelope" size="xs" color="primary" round flat @click="openEmailForm(props.row)" />
                                </q-item-label>
                                <q-item-label caption>
                                    <currency-format
                                        class="text-bold inline-block text-primary"
                                        :value="props.row.paid_amount"
                                        :unit="props.row.origin_currency"
                                        hide-white-spaces
                                        hide-zero-decimal
                                    />&nbsp;
                                    <q-badge rounded class="inline-block" color="green" v-if="props.row.transactions.accepted_incoming_count">
                                        {{props.row.transactions.accepted_incoming_count}}
                                        <q-tooltip content-class="bg-amber-1 text-black shadow-4" :offset="[10, 10]">
                                            Accepted incoming transaction count
                                        </q-tooltip>
                                    </q-badge>&nbsp;
                                    <q-badge rounded class="inline-block" color="grey" v-if="props.row.transactions.non_accepted_incoming_count">
                                        {{props.row.transactions.non_accepted_incoming_count}}
                                        <q-tooltip content-class="bg-amber-1 text-black shadow-4" :offset="[10, 10]">
                                            Non-accepted incoming transaction count
                                        </q-tooltip>
                                    </q-badge>
                                    <q-btn icon="fa fa-external-link" size="xs" color="primary" round flat
                                           @click.native="openURL(`${dashboard_link}/transfers/pay?ref=${props.row.reference}`)"
                                           target="_blank" />
                                </q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-td>
                    <q-td key="incoming_account" :props="props">
                        {{props.row.incoming_account}}
                        <q-popup-edit
                            v-model="props.row.rebit_account_id"
                            buttons
                            @save="(val, initialValue) => editAccount(val, initialValue, props.row, 'incoming_account')"
                            v-if="$auth.can('transfers.edit')"
                        >
                            <template v-slot="{ initialValue, value, emitValue, validate, set, cancel }">
                                <q-select
                                    v-model="props.row.rebit_account_id"
                                    :options="allRebitAccountsByCurrency(props.row.origin_currency_acronym)"
                                    fill-input
                                    option-value="id"
                                    option-label="label"
                                    clearable
                                    emit-value
                                    map-options
                                    :label="$t('transfers.table_titles.rebit_account')"
                                    color="primary"
                                    :value="props.row.rebit_account_id"
                                    @input="validateRebitAccount(initialValue, props.row)"
                                >
                                    <template v-slot:option="scope">
                                        <q-item
                                            v-bind="scope.itemProps"
                                            v-on="scope.itemEvents"
                                        >
                                            <q-item-section>
                                                <q-item-label>{{scope.opt.label}}</q-item-label>
                                                <q-item-label caption class="text-red" v-html="!scope.opt.is_active ? 'INACTIVE' : '&nbsp;'"></q-item-label>
                                            </q-item-section>

                                            <q-item-section side>
                                                <q-item-label class="text-grey">{{scope.opt.holder}}</q-item-label>
                                                <q-item-label caption class="text-red" v-html="!scope.opt.is_depositable ? 'NOT DEPOSITABLE' : '&nbsp;'"></q-item-label>
                                            </q-item-section>
                                        </q-item>
                                    </template>
                                </q-select>
                            </template>
                        </q-popup-edit>
                    </q-td>
                    <q-td key="outgoing_details" :props="props" class="no-padding">
                        <q-item>
                            <q-item-section avatar>
                                <q-btn round size="sm" color="primary" icon="fa fa-expand" @click="openPaymentDetailsModal(props.row)"/>
                            </q-item-section>
                            <q-item-section>
                                <q-item-label lines="1">
                                    <span v-clipboard="props.row.recipient_name" @click="$copied('Recipient\'s name copied!')">{{ props.row.recipient_name }}</span>
                                </q-item-label>
                                <q-item-label caption>
                                    <currency-format
                                        class="text-bold inline-block text-primary"
                                        :value="props.row.received_amount"
                                        :unit="props.row.currency"
                                        hide-white-spaces
                                        hide-zero-decimal
                                    />&nbsp;
                                    <q-badge rounded class="inline-block" color="green" v-if="props.row.transactions.accepted_outgoing_count">
                                        {{props.row.transactions.accepted_outgoing_count}}
                                        <q-tooltip content-class="bg-amber-1 text-black shadow-4" :offset="[10, 10]">
                                            Accepted outgoing transaction count
                                        </q-tooltip>
                                    </q-badge>&nbsp;
                                    <q-badge rounded class="inline-block" color="grey" v-if="props.row.transactions.non_accepted_outgoing_count">
                                        {{props.row.transactions.non_accepted_outgoing_count}}
                                        <q-tooltip content-class="bg-amber-1 text-black shadow-4" :offset="[10, 10]">
                                            Non-accepted outgoing transaction count
                                        </q-tooltip>
                                    </q-badge>
                                </q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-td>
                    <q-td key="rate" :props="props" class="no-padding">
                        <currency-format
                            class="inline-block text-bold"
                            :value="displayable_rate(props.row.rate, props.row.decimals)"
                            :unit="rate_reversed(props.row.rate) ? props.row.origin_currency : props.row.currency"
                            hide-white-spaces
                            hide-zero-decimal
                        />
                    </q-td>
                    <q-td key="status" :props="props" class="no-padding">
                        <q-skeleton type="QBadge" animation="pulse-y" v-if="statusLoading"/>
                        <div v-else>
                            <q-popup-edit
                                v-model="props.row.status"
                                buttons
                                @save="(val, initialValue) => editInline(val, initialValue, props.row, 'status')"
                            >
                                <template v-slot="{ initialValue, value, emitValue, validate, set, cancel }">
                                    <q-btn-dropdown
                                        :label="props.row.currentStatus.label"
                                        :icon="props.row.currentStatus.icon"
                                        :color="props.row.currentStatus.color"
                                        outline
                                        dense
                                    >
                                        <q-list bordered separator>
                                            <q-item clickable v-close-popup v-for="status in statuses" @click="props.row.status = status.name" :key="status.name">
                                                <q-item-section avatar>
                                                    <q-icon :name="status.icon" :color="status.color" text-color="white" />
                                                </q-item-section>
                                                <q-item-section>
                                                    <q-item-label :class="'text-'+status.color">{{ status.label }}</q-item-label>
                                                </q-item-section>
                                            </q-item>
                                        </q-list>
                                    </q-btn-dropdown>
                                </template>
                            </q-popup-edit>
                            <q-chip square
                                    :color="props.row.currentStatus.color"
                                    :icon="props.row.currentStatus.icon"
                                    text-color="white"
                                    style="cursor: pointer"
                            >{{props.row.currentStatus.label}}</q-chip>
                        </div>

                    </q-td>
                    <q-td key="type" :props="props">
                        <q-chip outline square color="blue" icon="fa fa-truck" v-if="props.row.type === 'standard'">Standard</q-chip>
                        <q-chip outline square color="pink" icon="fa fa-fighter-jet" v-if="props.row.type === 'asap'">ASAP</q-chip>
                    </q-td>
                    <q-td key="reason" :props="props">
                        {{props.row.reason}}
                    </q-td>
                    <q-td key="ift_date" :props="props">
                        <q-popup-edit v-model="props.row.ift_date" buttons
                                      @save="(val, initialValue) => editInline(val, initialValue, props.row, 'ift_date')"
                        >
                            <template v-slot="{ initialValue, value, emitValue, validate, set, cancel }">
                                <q-input v-model="props.row.ift_date" mask="date" :rules="['date']" clearable dense>
                                    <template v-slot:append>
                                        <q-icon name="event" class="cursor-pointer">
                                            <q-popup-proxy ref="qIftDate" transition-show="scale" transition-hide="scale">
                                                <q-date v-model="props.row.ift_date"
                                                        mask="YYYY-MM-DD"
                                                        today-btn
                                                        @input="() => $refs.qIftDate.hide()"
                                                />
                                            </q-popup-proxy>
                                        </q-icon>
                                    </template>
                                </q-input>
                            </template>
                        </q-popup-edit>
                        {{props.row.ift_date || '-'}}
                    </q-td>
                    <q-td key="updated_at" :props="props">
                        {{props.row.updated_at}}
                    </q-td>
                    <q-td key="notes" :props="props">
                        <div @click="openNotes(props.row)" v-if="props.row.notes_count" style="cursor: pointer">
                            <q-chip square :label="props.row.notes_count" color="primary" text-color="white" dense v-if="props.row.notes_count > 1"></q-chip>
                            <span v-html="props.row.notes"></span>
                        </div>
                        <div v-else><q-btn flat round size="sm" color="primary" icon="fa fa-message" @click="openNotes(props.row)" /></div>
                    </q-td>
                    <q-td key="setting" :props="props" v-if="$auth.can('transfers.edit')">
                        <q-btn flat round size="sm" color="orange" icon="fa fa-edit" @click="edit(props.row)"/>
                    </q-td>
                </q-tr>
                <q-tr v-show="props.expand" :props="props" transition-show="jump-down" transition-hide="jump-up">
                    <q-td colspan="100%" class="bg-amber-1">
                        <transfer-details :transfer="props.row" v-if="props.expand" @change="refresh(props.row)"/>
                    </q-td>
                </q-tr>
            </template>
            <!-- END CUSTOM COLUMNS -->
        </q-table>

        <total-ramps-amounts v-if="is_filtered" :on-ramps="on_ramps" :off-ramps="off_ramps"/>

        <email-modal
            v-model="displayEmailForm"
            :user-id="selectedRow.user.id"
            :content="selectedRow"
            v-if="$auth.can('users.send_email_when_transfer_status_changed') && selectedRow.user.id"
        />

        <outgoing-details-modal
            v-model="displayPaymentDetails"
            :reference="selectedRow.reference"
        />

        <crown-money-withdrawals-dialog
            v-model="displayCmWithdrawalDialog"
        />

        <receipts-dialog
            v-model="displayReceipts"
            :reference="selectedRow.reference"
            :transaction-id="0"
        />

        <notes-dialog
            v-model="displayNotesDialog"
            :noteable-id="selectedRow.reference"
            noteable-type="transfers"
            :dialog-title="`Notes: order ${selectedRow.reference}`"
            @made="noteSaved"
            @edited="noteSaved"
            v-if="$auth.can('transfers.notes') && selectedRow.reference"
        />

    </q-page>
</template>

<script>
import Transfer from '../models/Transfer.js'
import EmailForm from "../components/EmailForm"
import EmailModal from "../components/EmailModal"
import TransferDetails from "../components/TransferDetails"
import ReceiptsDialog from "../components/ReceiptsDialog.vue"
import PaymentInfo from "../components/history/PaymentInfo.vue"
import OutgoingDetailsModal from "../components/OutgoingDetailsModal"
import NotesDialog from "Modules/Note/resources/components/NotesDialog.vue"
import TotalRampsAmounts from "../components/statistics/TotalRampsAmounts.vue"
import CrownMoneyWithdrawalsDialog from "../components/CrownMoneyWithdrawals/CrownMoneyWithdrawalsDialog"
import {mapGetters, mapActions} from 'vuex'
import { openURL } from 'quasar'
import Ramp from "../../../Ramp/resources/models/Ramp.js";

export default {
    name: 'TransferList',
    props: {
        user: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        ...mapGetters(['exchange_rates', 'allRebitAccountsByCurrency', 'currencies']),
        columns() {
            const columns = [
                {name: 'reference', label: this.$t('transfers.table_titles.reference'), align: 'left', field: 'reference', sortable: true},
                {name: 'incoming_details', label: this.$t('transfers.table_titles.incoming_details'), align: 'left', field: 'user.name', sortable: false},
                {name: 'incoming_account', label: this.$t('transfers.table_titles.incoming_account'), align: 'left', field: 'incoming_account', sortable: false},
                {name: 'outgoing_details', label: this.$t('transfers.table_titles.outgoing_details'), align: 'left', field: 'recipient_name', sortable: false},
                {name: 'rate', label: this.$t('transfers.table_titles.rate'), align: 'left', field: 'rate', sortable: true},
                {name: 'status', label: this.$t('transfers.table_titles.status'), align: 'left', field: 'status', sortable: true},
                {name: 'type', label: this.$t('transfers.table_titles.type'), align: 'center', field: 'type', sortable: true},
                {name: 'reason', label: this.$t('transfers.table_titles.reason'), align: 'left', field: 'reason', sortable: false},
                {name: 'ift_date', label: this.$t('transfers.table_titles.ift_date'), align: 'left', field: 'ift_date', sortable: true},
                {name: 'updated_at', label: this.$t('transfers.table_titles.updated_at'), align: 'left', field: 'updated_at', sortable: true},
            ]

            if(this.$auth.can('transfers.notes')){
                this.selection = 'multiple'
                columns.push({name: 'notes', label: this.$t('transfers.table_titles.notes'), align: 'center', field: 'notes', sortable: false, classes: 'ellipsis', style: 'max-width: 180px', headerStyle: 'max-width: 180px'})
            }

            // add Edit/Delete column just if the user has appropriate permission
            if(this.$auth.can('transfers.edit')){
                this.selection = 'multiple'
                columns.push({name: 'setting', align: 'center', label: this.$t('transfers.table_titles.setting'), sortable: false, field: 'setting'})
            }

            return columns
        },
        middleName () {
            return this.user.middle_name !== null ? this.user.middle_name : ''
        },
        full_name() {
            if (!this.user.first_name) {
                return this.user.email
            }
            return this.user.last_name !== null ? this.user.first_name + ' ' + this.middleName +  ' ' + this.user.last_name :
                this.user.first_name
        },
        selecting_range: {
            get: function() {
                return this.date_range
            },
            set: function(value) {
                if(value && typeof value === 'string') {
                    this.date_range = {from: value, to: value}
                } else if(value && typeof value === 'object' && value.from) {
                    this.date_range = value
                } else {
                    this.date_range = {from: '', to: ''}
                }
            }
        },
        is_filtered() {
            return this.date_range.from || this.filtered_statuses.length !== 6 || this.ramp
        },
        origins() {
            return this.currencies.map(currency => {return currency.acronym})
        },
        destinations() {
            return this.currencies.map(currency => {return currency.acronym})
        },
        rate_reversed() {
            return rate => rate < 0.1
        },
        displayable_rate() {
            return (rate, decimals) => Number(this.rate_reversed(rate) ? (1 / rate) : rate).roundToX(decimals)
        },
        dashboard_link() {
            return process.env.DASHBOARD_URL
        }
    },
    data: () => ({
        separator: 'horizontal',
        filter: '',
        loading: false,
        statusLoading: false,
        serverPagination: {
            page: 1,
            sortBy: 'created_at',
            rowsPerPage: 50,
            descending: true,
            rowsNumber: 10 // specifying this determines pagination is server-side
        },
        visibleColumns: ['reference', 'incoming_details', 'incoming_account', 'outgoing_details', 'rate', 'status', 'notes', 'setting'],
        serverData: [],
        on_ramps: [],
        off_ramps: [],
        selection: 'none',
        selectedRows: [],
        statuses: [
            {name:'awaiting', label: 'Awaiting approval', icon: 'fa fa-hourglass-half', color: 'warning'},
            {name:'payment_issue', label: 'Payment issue', icon: 'fa fa-download', color: 'pink'},
            {name:'paid', label: 'Paid', icon: 'fa fa-file-invoice-dollar', color: 'blue'},
            {name:'transfer_issue', label: 'Transfer issue', icon: 'fa fa-upload', color: 'pink'},
            {name:'transferred', label: 'Done', icon: 'fa fa-check-circle', color: 'positive'},
            {name:'canceled', label: 'Cancelled', icon: 'fa fa-window-close', color: 'negative'},
        ],
        filtered_statuses: ['awaiting', 'payment_issue','paid','transfer_issue'],
        date_range: {from:'', to:''},
        currentStatusObj: null,
        displayEmailForm: false,
        displayPaymentDetails: false,
        displayCmWithdrawalDialog: false,
        ramp: '',
        origin: '',
        dest: '',
        selectedRow: {
            user: {
                id: 0
            },
            reference: ''
        },
        notify_user: false,
        displayReceipts: false,
        displayNotesDialog: false
    }),
    methods: {
        openURL,
        ...mapActions(['set_currencies']),
        all({pagination, filter, statuses, ramp}) {
            this.loading = true
            Transfer.all(`${this.user.id ? `user_id=${this.user.id}&` : ''}filter=${filter}&statuses=${this.filtered_statuses}&origin=${this.origin !== null ? this.origin: ''}&dest=${this.dest !== null ? this.dest: ''}&date_range=${this.date_range.from},${this.date_range.to}&sort=${pagination.sortBy}%7C${pagination.descending ? 'desc' : 'asc'}&page=${pagination.page}&per_page=${pagination.rowsPerPage}`, ({data, meta, on_ramps, off_ramps}) => {
                this.serverPagination = pagination
                // we also set (or update) rowsNumber
                this.serverPagination.rowsNumber = meta.total
                this.serverData = data
                this.on_ramps = on_ramps
                this.off_ramps = off_ramps
                this.loading = false
            }).catch(error => {
                this.$q.notify({
                    message: error.data.error,
                    color: 'negative',
                    icon: 'fa fa-exclamation-triangle'
                })
                this.loading = false
            })
        },
        cmIncomingPayments() {
            this.loading = true
            Transfer.cmIncomingPayments(() => {
                this.$q.notify({
                    message: 'All Crown Money transactions fetched successfully!',
                })
                this.loading = false
            })
        },
        details(props) {
            props.expand = !props.expand
        },
        editAccount(val, initVal, row, col) {
            this.$q.dialog({
                title: 'Rebit account updated',
                message: 'Notify user by email?',
                ok: 'Notify',
                cancel: 'Cancel',
                persist: true
            }).onOk(() => {
                this.notify_user = true
                this.editInline(val, initVal, row, col)
            }).onCancel(() => {
                this.editInline(val, initVal, row, col)
            })
        },
        editInline(val, initVal, row, col) {
            row[col] = val
            if (col === 'incoming_account') {
                const rebit_accounts = this.allRebitAccountsByCurrency(row.origin_currency_acronym)
                const selected_rebit_account = rebit_accounts.find(rebit_account => rebit_account.id === val)
                row[col] = selected_rebit_account ? selected_rebit_account.label : '⚠️ Needs Account'
            }
            this.loading = true
            new Transfer({
                reference: row.reference,
                status: col === 'status' ? val : row.status,
                ift_date: col === 'ift_date' ? val : row.ift_date,
                rebit_account_id: col === 'incoming_account' ? val : row.rebit_account_id,
                notify_user: this.notify_user
            }).updateInline((data) => {
                for (let field in data) {
                    row[field] = data[field]
                }
                this.loading = false
                this.notify_user = false
            }).catch(({data}) => {
                row[col] = initVal
                this.loading = false
                this.notify_user = false
            })
        },
        refresh(row) {
            this.statusLoading = true
            new Transfer({
                reference:row.reference,
            }).refreshStatus((status) => {
                row['status'] = status
                this.statusLoading = false
            }).catch(({data}) => {
                this.statusLoading = false
            })
        },
        copied(message) {
            this.$q.notify({
                message: message,
                position: 'top'
            })
        },
        openReceipts(row) {
            this.displayReceipts = true
            this.selectedRow = row
        },
        openEmailForm(row) {
            this.displayEmailForm = true
            this.selectedRow = row
        },
        openPaymentDetailsModal(row) {
            this.displayPaymentDetails = true
            this.selectedRow = row
        },
        openNotes(row) {
            this.refreshNotesKey += 1
            this.displayNotesDialog = true
            this.selectedRow = row
        },
        noteSaved(note) {
            this.selectedRow['notes'] = note
        },
        myTweak (offset) {
            return { minHeight: offset ? `calc(35vh - ${offset}px)` : '35vh' }
        },
        removeDateRangeFilter() {
            this.date_range = {from: '', to: ''}
            this.all({
                pagination: this.serverPagination,
                filter: this.filter
            })
        },
        edit(value) {
            this.$router.push({name: 'transfers.edit', params: {reference: value.reference}})
        },
        validateRebitAccount(initialValue, row) {
            const selected_rebit_account = this.allRebitAccountsByCurrency(row.origin_currency_acronym).find(account => account.id === row.rebit_account_id)

            if (selected_rebit_account && !(selected_rebit_account.is_depositable && selected_rebit_account.is_active)) {
                this.$q.dialog({
                    title: 'ATTENTION',
                    message: "Inactive or Not-depositable account.<br>Are you sure?",
                    html: true,
                    cancel: true,
                    persistent: true
                }).onCancel(() => {
                    row.rebit_account_id = initialValue
                })
            }
        }
    },
    beforeCreate() {
        if(! this.$auth.can('transfers.list')){
            this.$router.push({name: 'dashboard'})
        }
    },
    created() {
        if (! this.currencies.length) {
            this.loading = true
            this.set_currencies().then(() => {this.loading = false}).catch(() => {this.loading = false})
        }
    },
    mounted() {
        // once mounted, we need to trigger the initial server data fetch
        if(this.$router.currentRoute.query.q !== undefined && !this.$parent.name) {
            this.filter = this.$router.currentRoute.query.q
        } else {
            this.all({
                pagination: this.serverPagination,
                filter: this.filter
            })
        }
    },
    components: {
        NotesDialog,
        ReceiptsDialog,
        OutgoingDetailsModal,
        TransferDetails,
        PaymentInfo,
        EmailModal,
        EmailForm,
        TotalRampsAmounts,
        CrownMoneyWithdrawalsDialog
    }
}
</script>
